import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Plus, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import { erroreDi } from "@/lib/crm";
import { useMedia } from "@/hooks/use-media";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ListRow } from "@/components/ui/list-row";
import { SegmentedButtons } from "@/components/ui/segmented";
import { EmptyState, ErrorState, ListSkeleton } from "@/components/ui/states";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AssistenzaDialog, useMontatore, usePersone } from "@/components/assistenza/Assistenze";
import DettaglioAssistenza, { quandoRiga } from "@/components/assistenza/Dettaglio";

const TUTTE = "tutte";
const FILTRI = { aperte: (a) => a.stato !== "fatta", fatte: (a) => a.stato === "fatta", tutte: () => true };
/* L'ordine dei gruppi è quello dell'urgenza; dentro ogni gruppo vale l'ordine del server. */
const GRUPPI = [
  { id: "scadute", label: "Scadute", colore: "text-danger-ink", vale: (a) => a.scaduta },
  { id: "da_fare", label: "Da fare", colore: "text-warning-ink", vale: (a) => a.stato === "da_fare" && !a.scaduta },
  { id: "programmate", label: "Programmate", colore: "text-brand-ink", vale: (a) => a.stato === "programmata" && !a.scaduta },
  { id: "fatte", label: "Fatte", colore: "text-success-ink", vale: (a) => a.stato === "fatta" },
];

function Riga({ a, oggi, scelta, onScegli }) {
  const senzaNessuno = a.stato !== "fatta" && !a.assegnato;
  return (
    <ListRow
      data-testid={`assistenza-${a.id}`}
      selected={scelta}
      onClick={() => onScegli(a.id)}
      title={a.descrizione}
      meta={a.sede ? `${a.cliente} · ${a.sede}` : a.cliente}
      trailing={
        <span className="grid justify-items-end text-body">
          <span className={cn("font-semibold", a.scaduta ? "text-danger-ink" : "text-ink-strong")}>{quandoRiga(a, oggi)}</span>
          <span className={cn("text-small", senzaNessuno ? "font-semibold text-warning-ink" : "font-normal text-ink-2")}>
            {a.assegnato || "Da assegnare"}
          </span>
        </span>
      }
    />
  );
}

/**
 * Tutte le assistenze: elenco a sinistra, dettaglio a destra. Chi vede quali lo decide il
 * database (0039): il venditore quelle dei suoi clienti, il montatore le sue. Sotto i
 * 1024 px il dettaglio prende il posto dell'elenco e «Assistenza» riporta indietro.
 */
export default function AssistenzaPage() {
  const [vista, setVista] = useState("aperte");
  const [sede, setSede] = useState(TUTTE);
  const [chi, setChi] = useState(TUTTE);
  const [sedi, setSedi] = useState([]);
  const montatore = useMontatore();
  const persone = usePersone(undefined, !montatore);
  const [dati, setDati] = useState(null); // { oggi, righe }
  const [errore, setErrore] = useState(null);
  const [scelta, setScelta] = useState(null);
  const [aperta, setAperta] = useState(undefined); // undefined = chiuso, null = nuova, riga = modifica
  const largo = useMedia("(min-width: 1024px)");

  useEffect(() => {
    api.get("/sedi").then((r) => setSedi(r.data || [])).catch(() => setSedi([]));
  }, []);

  /* L'elenco si chiede intero: «Aperte» e «Fatte» si dividono qui, e i due numeri restano veri. */
  const carica = useCallback(() => {
    const params = {};
    if (sede !== TUTTE) params.sede_id = sede;
    if (chi !== TUTTE) params.assegnato_a = chi;
    api
      .get("/assistenze", { params })
      .then((r) => {
        setDati({ oggi: r.data?.oggi, righe: r.data?.righe || [] });
        setErrore(null);
      })
      .catch((e) => setErrore(erroreDi(e)));
  }, [sede, chi]);
  useEffect(carica, [carica]);

  /* «Nuovo › Assistenza» dal guscio arriva con ?nuova=1: apre «Nuova assistenza» (non al montatore). */
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("nuova") !== "1") return;
    if (!montatore) setAperta(null);
    setParams({}, { replace: true });
  }, [params, setParams, montatore]);

  const righe = dati?.righe;
  const conta = (v) => (righe || []).filter(FILTRI[v]).length;
  const visibili = useMemo(() => (righe || []).filter(FILTRI[vista]), [righe, vista]);
  const gruppi = useMemo(
    () => GRUPPI.map((g) => ({ ...g, righe: visibili.filter((a) => GRUPPI.find((x) => x.vale(a)) === g) })).filter((g) => g.righe.length),
    [visibili],
  );
  const filtrata = sede !== TUTTE || chi !== TUTTE;

  /* Sul computer il dettaglio non resta mai vuoto: se la scelta sparisce (fatta, filtrata) passa alla prima. */
  useEffect(() => {
    if (!righe || (scelta && visibili.some((a) => a.id === scelta))) return;
    setScelta(largo && visibili[0] ? visibili[0].id : null);
  }, [righe, visibili, scelta, largo]);
  const corrente = visibili.find((a) => a.id === scelta);

  const fatto = (msg) => {
    toast.success(msg);
    carica();
  };
  // Il montatore non crea assistenze (la RLS glielo nega): niente bottone che non porta a niente.
  const nuova = (testId, etichetta = "Nuova assistenza") =>
    montatore ? null : (
      <Button onClick={() => setAperta(null)} data-testid={testId} className="max-md:w-full">
        <Plus />
        {etichetta}
      </Button>
    );

  const soloDettaglio = !largo && corrente;

  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-card" data-testid="page-assistenza">
      {soloDettaglio ? null : (
        <>
          <header className="flex flex-col gap-group lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <h1 className="font-display text-h1-mobile text-ink-strong md:text-h1">Assistenza</h1>
              <p className="mt-micro text-body text-ink-2">
                {montatore ? "Le assistenze assegnate a te: cosa, dove e quando." : "Per ogni cliente già servito: cosa c'è da fare, dove, quando e chi la fa."}
              </p>
            </div>
            {nuova("btn-nuova-assistenza")}
          </header>

          <div className="flex flex-col gap-inside md:flex-row md:flex-wrap md:items-center" role="group" aria-label="Filtri dell'assistenza">
            <SegmentedButtons
              voci={[
                { id: "aperte", label: `Aperte${righe ? ` ${conta("aperte")}` : ""}` },
                { id: "fatte", label: `Fatte${righe ? ` ${conta("fatte")}` : ""}` },
                { id: "tutte", label: "Tutte" },
              ]}
              valore={vista}
              onCambia={setVista}
              prefisso="filtro-stato"
              aria-label="Stato"
              className="shrink-0 whitespace-nowrap md:w-auto"
            />
            <div className="grid grid-cols-2 gap-inside md:flex">
              <Select value={sede} onValueChange={setSede}>
                <SelectTrigger className="md:h-9 md:w-48" data-testid="filtro-sede" aria-label="Sede">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TUTTE}>Tutte le sedi</SelectItem>
                  {sedi.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {montatore ? null : (
                <Select value={chi} onValueChange={setChi}>
                  <SelectTrigger className="md:h-9 md:w-48" data-testid="filtro-chi" aria-label="Assegnata a">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TUTTE}>Chiunque</SelectItem>
                    {persone.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </>
      )}

      {errore && !righe ? (
        <div className="rounded-lg border border-line bg-surface" data-testid="assistenza-errore">
          <ErrorState title="Non riesco a caricare le assistenze" actions={<Button onClick={carica}>Riprova</Button>}>
            {errore}
          </ErrorState>
        </div>
      ) : null}
      {errore && righe ? (
        <p role="alert" className="rounded-md bg-danger-bg px-group py-inside text-body text-danger-ink" data-testid="assistenza-errore">
          {errore}
        </p>
      ) : null}

      {!righe && !errore ? (
        <div className="rounded-lg border border-line bg-surface" aria-busy="true" data-testid="assistenza-caricamento">
          <ListSkeleton rows={4} label="Carico le assistenze…" />
        </div>
      ) : null}

      {righe && !visibili.length ? (
        <div className="rounded-lg border border-line bg-surface" data-testid={filtrata ? "assistenza-vuota-filtri" : "assistenza-vuota"}>
          {filtrata ? (
            <EmptyState
              icon={Wrench}
              title="Nessuna assistenza con questi filtri"
              actions={
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSede(TUTTE);
                    setChi(TUTTE);
                  }}
                >
                  Togli i filtri
                </Button>
              }
            >
              Cambia sede o persona per vedere le altre.
            </EmptyState>
          ) : (
            <EmptyState
              icon={Wrench}
              title={montatore ? "Nessuna assistenza assegnata a te" : vista === "fatte" ? "Nessun intervento fatto" : "Nessun intervento aperto"}
              actions={
                <>
                  {vista === "aperte" ? nuova("assistenza-vuota-nuova", "Apri un intervento") : null}
                  {vista === "aperte" && conta("fatte") ? (
                    <Button variant="secondary" onClick={() => setVista("fatte")} data-testid="assistenza-vedi-fatte">
                      {conta("fatte") === 1 ? "Vedi quella fatta" : `Vedi le ${conta("fatte")} fatte`}
                    </Button>
                  ) : null}
                </>
              }
            >
              {montatore
                ? "Quando ti assegnano un intervento, lo trovi qui con l'indirizzo e la data."
                : "Quando un cliente già servito segnala un problema, aprilo qui: cosa c'è da fare, dove, quando e chi lo fa."}
            </EmptyState>
          )}
        </div>
      ) : null}

      {righe && visibili.length ? (
        <div className="grid items-start gap-card lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          {soloDettaglio ? null : (
            <div className="overflow-hidden rounded-lg border border-line bg-surface" data-testid="assistenza-elenco">
              {gruppi.map((g) => (
                <section key={g.id} aria-label={g.label} className="[&:first-child>h2]:border-t-0">
                  <h2 className={cn("border-y border-line-subtle bg-surface-2 px-4 py-2.5 text-caption", g.colore)}>
                    {g.label} · {g.righe.length}
                  </h2>
                  <div className="[&>[data-slot=list-row]:last-child]:border-b-0">
                    {g.righe.map((a) => (
                      <Riga key={a.id} a={a} oggi={dati.oggi} scelta={a.id === scelta} onScegli={setScelta} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
          {corrente ? (
            <div className="lg:sticky lg:top-card">
              <DettaglioAssistenza
                a={corrente}
                oggi={dati.oggi}
                montatore={montatore}
                onFatto={fatto}
                onModifica={setAperta}
                onIndietro={largo ? null : () => setScelta(null)}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      <AssistenzaDialog
        open={aperta !== undefined}
        onOpenChange={(v) => !v && setAperta(undefined)}
        assistenza={aperta || null}
        onFatto={fatto}
      />
    </section>
  );
}
